"use client";

import { CommandGroup } from "@universe/ui/components/command";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import type { ProductType } from "@/constants";
import { useBalances } from "@/hooks/useBalances";
import useBaseCurrencyConverter from "@/hooks/useBaseCurrencyConverter";
import { useCapabilities } from "@/hooks/useCapabilities";
import useCurrencyFormatter from "@/hooks/useCurrencyFormatter";
import { useCustomerAccount, useGlobalData } from "@/providers";
import { compareCurrencyRank } from "@/utils/sortProducts";

import { AccountSelectItem } from "./account-select-item";
import { AccountSelectPopover } from "./account-select-popover";

type TradableAccountsSelectProps = {
  className?: string;
  placeholder?: string;
  currentAccountId?: string;
  onSelect?: (accountId: string) => void;
  showFlag?: boolean;
  flagSize?: string;
  popoverAlign?: "start" | "center" | "end";
  overrideBaseCurrency?: string;
  fixedCurrency?: boolean;
  allowZeroBalance?: boolean;
  /** When true, only show the currency code (not the name) in the trigger */
  showCodeOnly?: boolean;
};

type TradableAccountItem = {
  value: string;
  label: string;
  name: string;
  searchIndex: string;
  balance: number;
  type: ProductType;
  disabled: boolean;
  disabledReason?: string;
};

/**
 * Account select component for the exchange page.
 * Shows tradable products with their balances (or 0 if no balance).
 */
export function TradableAccountsSelect({
  className,
  placeholder,
  currentAccountId,
  onSelect,
  showFlag = false,
  flagSize = "28px",
  popoverAlign = "start",
  overrideBaseCurrency,
  fixedCurrency = false,
  allowZeroBalance = false,
  showCodeOnly = false,
}: TradableAccountsSelectProps) {
  const tAccounts = useTranslations("accounts");
  const [open, setOpen] = useState(false);
  // Use local state only for user selections, prefer currentAccountId from props
  const [localValue, setLocalValue] = useState("");
  // Derive value: prefer prop from parent, fallback to local selection
  const value = currentAccountId || localValue;

  const { tradable, isLoading: isCapabilitiesLoading } = useCapabilities();
  const { balances, isLoading: isBalancesLoading } = useBalances();
  const { products } = useGlobalData();
  const { formatCurrency } = useCurrencyFormatter();
  const { convert } = useBaseCurrencyConverter();
  const { customerAccountBaseCurrency } = useCustomerAccount();
  const baseCurrency = overrideBaseCurrency || customerAccountBaseCurrency;

  const isLoading = isCapabilitiesLoading || isBalancesLoading;

  // Build list of tradable accounts with balances
  const accountsList = useMemo(() => {
    const items: TradableAccountItem[] = tradable.map((product) => {
      const productInfo = products?.find((p) => p.code === product.id);
      const balanceInfo = balances[product.id];
      const balance = balanceInfo?.balance ?? 0;

      // Determine if trading is disabled
      const isDisabled = !product.isTradable || !!product.tradingRestriction;

      let disabledReason: string | undefined;
      if (isDisabled) {
        if (product.tradingRestriction) {
          // Use the enum value directly as translation key
          disabledReason = tAccounts(
            `tradingRestriction.${product.tradingRestriction}`
          );
        } else {
          disabledReason = tAccounts("unavailable");
        }
      }

      return {
        value: product.id,
        label: formatCurrency(balance, product.id) ?? product.id,
        name: productInfo?.name || product.id,
        type: product.resourceType,
        searchIndex: `${product.id}-${productInfo?.name || product.id}`,
        balance,
        disabled: isDisabled,
        disabledReason,
      };
    });

    // Sort: baseCurrency first, then tradable with balance, then tradable with 0 balance, then non-tradable
    return items.sort((a, b) => {
      // Base currency always first
      if (a.value === baseCurrency) return -1;
      if (b.value === baseCurrency) return 1;

      // Non-tradable items go to the bottom
      if (a.disabled !== b.disabled) {
        return a.disabled ? 1 : -1;
      }

      // Within tradable items: sort by balance (items with balance first)
      if (!a.disabled && !b.disabled) {
        const hasBalanceA = a.balance > 0;
        const hasBalanceB = b.balance > 0;

        if (hasBalanceA !== hasBalanceB) {
          return hasBalanceA ? -1 : 1;
        }

        // Both have balance: sort by balance value (highest first)
        if (hasBalanceA && hasBalanceB) {
          const balanceA = convert(a.balance, a.value);
          const balanceB = convert(b.balance, b.value);
          if (balanceB !== balanceA) {
            return balanceB - balanceA;
          }
        }
      }

      // Fallback: fiat majors, then other fiat, then blockchain
      return compareCurrencyRank(
        { code: a.value, type: a.type },
        { code: b.value, type: b.type }
      );
    });
  }, [
    tradable,
    balances,
    products,
    formatCurrency,
    convert,
    baseCurrency,
    tAccounts,
  ]);

  const selectedAccount = accountsList.find(
    (account) => account.value === value
  );

  // When fixedCurrency is true and the currency isn't in the tradable list,
  // we still need to display it (e.g., for payment flows where beneficiary dictates currency)
  const displayValue =
    selectedAccount?.value || (fixedCurrency ? value : undefined);
  const displayName =
    selectedAccount?.name ||
    (fixedCurrency && value
      ? products?.find((p) => p.code === value)?.name
      : undefined);

  const handleSelect = (accountId: string) => {
    // Close popover first to prevent re-render issues during parent state updates
    setOpen(false);
    setLocalValue(accountId);
    onSelect?.(accountId);
  };

  return (
    <AccountSelectPopover
      open={open}
      onOpenChange={setOpen}
      isLoading={isLoading}
      className={className}
      popoverAlign={popoverAlign}
      selectedValue={displayValue}
      selectedName={displayName}
      showFlag={showFlag}
      flagSize={flagSize}
      placeholder={placeholder}
      fixedCurrency={fixedCurrency}
      showCodeOnly={showCodeOnly}
    >
      <CommandGroup>
        {accountsList.map((account) => {
          const isItemDisabled =
            account.disabled || (account.balance === 0 && !allowZeroBalance);

          return (
            <AccountSelectItem
              key={account.searchIndex}
              value={account.value}
              label={account.label}
              name={account.name}
              searchIndex={account.searchIndex}
              isSelected={value === account.value}
              onSelect={() => handleSelect(account.value)}
              disabled={isItemDisabled}
              disabledReason={account.disabledReason}
            />
          );
        })}
      </CommandGroup>
    </AccountSelectPopover>
  );
}
